import { useCallback, useEffect, useRef, useState } from 'react'
import {
  DatabaseBackup, Download, Trash2, Upload, RefreshCw, PlayCircle,
  Clock, HardDriveDownload, AlertTriangle, ShieldCheck,
} from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { KpiCard } from '@/components/ui/KpiCard'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { EmptyState } from '@/components/ui/EmptyState'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { toast } from '@/components/ui/Toast'
import { dateTimeBR } from '@/lib/format'
import { extractApiError } from '@/lib/api-errors'
import { useAuthority } from '@/hooks/useAuthority'
import {
  BackupsService, humanCron, humanDuration, humanSize, RESTORE_CONFIRMATION,
  type BackupFile, type BackupRun, type BackupsOverview,
} from '@/services/modules/BackupsService'

const RUN_STATUS: Record<string, { label: string; variant: 'green' | 'amber' | 'red' | 'gray' }> = {
  CONCLUIDO:   { label: 'Concluído',   variant: 'green' },
  PROCESSANDO: { label: 'Em andamento', variant: 'amber' },
  PENDENTE:    { label: 'Na fila',     variant: 'gray' },
  FALHOU:      { label: 'Falhou',      variant: 'red' },
}

export function IntegrationsBackupsPage() {
  const { can } = useAuthority()
  const canRun = can('integrations.backups.run')
  const canRestore = can('integrations.backups.restore')
  const canDelete = can('integrations.backups.delete')

  const [data, setData] = useState<BackupsOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [toRestore, setToRestore] = useState<BackupFile | null>(null)
  const [confirmation, setConfirmation] = useState('')
  const [toDelete, setToDelete] = useState<BackupFile | null>(null)
  const uploadRef = useRef<HTMLInputElement | null>(null)

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    try {
      setData(await BackupsService.overview())
    } catch (e) {
      toast.error({ title: 'Erro ao carregar', description: extractApiError(e).message })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  // Enquanto houver dump ou restauração rodando, a tela se atualiza sozinha:
  // são operações de minutos e o operador não deve ficar recarregando na mão.
  useEffect(() => {
    if (!data?.busy) return
    const t = setInterval(() => void load(true), 5000)
    return () => clearInterval(t)
  }, [data?.busy, load])

  async function runBackup() {
    setBusy(true)
    try {
      await BackupsService.run()
      toast.success({ title: 'Backup iniciado', description: 'O dump roda em segundo plano; a tela acompanha sozinha.' })
      await load(true)
    } catch (e) {
      toast.error({ title: 'Não foi possível iniciar', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  async function download(f: BackupFile) {
    try {
      const url = await BackupsService.downloadUrl(f.path)
      if (!url) throw new Error('Não foi possível gerar o link.')
      window.open(url, '_blank', 'noopener')
    } catch (e) {
      toast.error({ title: 'Erro ao baixar', description: extractApiError(e).message })
    }
  }

  async function upload(file: File) {
    setBusy(true)
    try {
      await BackupsService.upload(file)
      toast.success({ title: 'Arquivo enviado', description: 'O dump já aparece na lista e pode ser restaurado.' })
      await load(true)
    } catch (e) {
      // 413 vem do nginx, não do Laravel: a resposta é HTML e o extractApiError
      // não tem o que ler. Sem esta tradução o operador vê um erro sem sentido.
      const status = (e as { response?: { status?: number } })?.response?.status
      toast.error({
        title: status === 413 ? 'Arquivo grande demais' : 'Erro ao enviar',
        description: status === 413
          ? 'O servidor recusou o envio pelo tamanho. Use o terminal: php artisan db:backup-import <arquivo>.dump'
          : extractApiError(e).message,
      })
    } finally {
      setBusy(false)
      if (uploadRef.current) uploadRef.current.value = ''
    }
  }

  async function confirmRestore() {
    if (!toRestore) return
    setBusy(true)
    try {
      await BackupsService.restore(toRestore.path, confirmation.trim())
      toast.success({
        title: 'Restauração iniciada',
        description: 'Um backup do estado atual é gerado antes de substituir a base.',
      })
      setToRestore(null); setConfirmation('')
      await load(true)
    } catch (e) {
      toast.error({ title: 'Não foi possível restaurar', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  async function confirmDelete() {
    if (!toDelete) return
    setBusy(true)
    try {
      await BackupsService.destroy(toDelete.path)
      toast.success({ title: 'Arquivo removido', description: `${toDelete.name} saiu do bucket.` })
      setToDelete(null)
      await load(true)
    } catch (e) {
      toast.error({ title: 'Não foi possível excluir', description: extractApiError(e).message })
    } finally { setBusy(false) }
  }

  const fileCols: DataTableColumn<BackupFile>[] = [
    { key: 'name', header: 'Arquivo', className: 'w-full', render: (f) => (
      <span className="font-mono text-xs text-gray-700">{f.name}</span>
    )},
    { key: 'size', header: 'Tamanho', align: 'right', className: 'w-32', render: (f) => humanSize(f.size) },
    { key: 'createdAt', header: 'Gerado em', align: 'center', className: 'w-44',
      render: (f) => f.createdAt ? dateTimeBR(f.createdAt) : '—' },
  ]

  const runCols: DataTableColumn<BackupRun>[] = [
    { key: 'kind', header: 'Operação', className: 'w-32', render: (r) => (
      <Badge variant={r.kind === 'RESTORE' ? 'amber' : 'blue'}>
        {r.kind === 'RESTORE' ? 'Restauração' : 'Backup'}
      </Badge>
    )},
    { key: 'trigger', header: 'Origem', align: 'center', className: 'w-28',
      render: (r) => <span className="text-xs text-gray-500">{r.trigger === 'AGENDADO' ? 'Automático' : 'Manual'}</span> },
    { key: 'file', header: 'Arquivo', className: 'w-full', render: (r) => (
      <span className="font-mono text-[11px] text-gray-600">{r.fileName ?? '—'}</span>
    )},
    { key: 'size', header: 'Tamanho', align: 'right', className: 'w-28', render: (r) => humanSize(r.fileSize) },
    { key: 'duration', header: 'Duração', align: 'right', className: 'w-28', render: (r) => humanDuration(r.durationSeconds) },
    { key: 'user', header: 'Quem', align: 'center', className: 'w-40',
      render: (r) => <span className="text-xs text-gray-500">{r.userName ?? 'sistema'}</span> },
    { key: 'when', header: 'Quando', align: 'center', className: 'w-44',
      render: (r) => <span className="text-xs">{dateTimeBR(r.createdAt)}</span> },
    { key: 'status', header: 'Status', align: 'center', className: 'w-32', render: (r) => {
      const s = RUN_STATUS[r.status] ?? { label: r.status, variant: 'gray' as const }
      return (
        <span title={r.error ?? undefined}>
          <Badge variant={s.variant}>{s.label}</Badge>
        </span>
      )
    }},
  ]

  const storage = data?.storage
  const busyRun = data?.busy

  return (
    <>
      <PageHeader
        title="Backups"
        subtitle="Rotinas automáticas, arquivos guardados no S3 e restauração da base"
        crumbs={[{ label: 'Integrações' }, { label: 'Backups' }]}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" iconLeft={<RefreshCw size={14} />}
              onClick={() => void load()} loading={loading}>Atualizar</Button>
            {canRun && (
              <>
                <input
                  ref={uploadRef} type="file" accept=".dump" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f) }}
                />
                <Button variant="secondary" size="sm" iconLeft={<Upload size={14} />}
                  disabled={busy || !!busyRun} onClick={() => uploadRef.current?.click()}>
                  Enviar dump
                </Button>
                <Button size="sm" iconLeft={<PlayCircle size={14} />}
                  loading={busy} disabled={!!busyRun} onClick={() => void runBackup()}>
                  Gerar backup agora
                </Button>
              </>
            )}
          </div>
        }
      />

      {busyRun && (
        <div className="mb-4 flex items-center gap-3 rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-amber-200">
          <RefreshCw size={16} className="animate-spin text-amber-600" />
          <span>
            {busyRun.kind === 'RESTORE'
              ? 'Restauração em andamento. Não use o sistema até terminar — a base está sendo substituída.'
              : 'Backup em andamento. A tela atualiza sozinha quando terminar.'}
          </span>
        </div>
      )}

      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
        <KpiCard label="Backups guardados" value={String(storage?.count ?? 0)}
          icon={<DatabaseBackup size={18} />} hint={`Mantém os ${storage?.keep ?? 30} mais recentes`} />
        <KpiCard label="Espaço ocupado" value={humanSize(storage?.totalSize)}
          icon={<HardDriveDownload size={18} />} hint={storage?.prefix} accent="violet" />
        <KpiCard label="Último backup" value={
          data?.runs.find(r => r.kind === 'BACKUP' && r.status === 'CONCLUIDO')?.finishedAt
            ? dateTimeBR(data.runs.find(r => r.kind === 'BACKUP' && r.status === 'CONCLUIDO')!.finishedAt)
            : '—'
        } icon={<Clock size={18} />} accent="green" />
      </div>

      <Card className="mb-4">
        <CardHeader
          title="Rotinas automáticas"
          subtitle="Lidas do agendador do sistema — o que está aqui é o que roda de fato no servidor"
        />
        <CardBody>
          {(data?.schedules.length ?? 0) === 0 ? (
            <EmptyState title="Nenhuma rotina agendada" description="O agendador não tem tarefas registradas." />
          ) : (
            <ul className="divide-y divide-gray-100">
              {data!.schedules.map((s) => (
                <li key={s.command + s.expression} className="flex items-start gap-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs text-gray-700">{s.command}</span>
                      {s.isBackup && <Badge variant="green">backup do banco</Badge>}
                    </div>
                    {s.description && <p className="text-[11px] text-gray-500">{s.description}</p>}
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-xs text-gray-700">{humanCron(s.expression)}</div>
                    {s.nextRunAt && (
                      <div className="text-[11px] text-gray-400">próxima: {dateTimeBR(s.nextRunAt)}</div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <Card className="mb-4">
        <CardHeader
          title="Arquivos disponíveis"
          subtitle={`Guardados em ${storage?.prefix ?? 'backups/database'}. A rotina apaga os mais antigos ao passar de ${storage?.keep ?? 30}.`}
        />
        <CardBody>
          <DataTable
            columns={fileCols}
            rows={data?.files ?? []}
            loading={loading}
            getRowKey={(f) => f.path}
            emptyMessage="Nenhum backup no bucket ainda."
            rowActions={(f) => [
              { key: 'download', label: 'Baixar', icon: <Download size={14} />, onClick: () => void download(f) },
              ...(canRestore ? [{
                key: 'restore', label: 'Restaurar banco com este arquivo',
                icon: <HardDriveDownload size={14} />, divider: true,
                onClick: () => { setToRestore(f); setConfirmation('') },
              }] : []),
              ...(canDelete ? [{
                key: 'delete', label: 'Excluir arquivo', icon: <Trash2 size={14} />,
                danger: true, divider: !canRestore, onClick: () => setToDelete(f),
              }] : []),
            ]}
          />
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Histórico" subtitle="Últimas 50 execuções, automáticas e manuais" />
        <CardBody>
          <DataTable
            columns={runCols}
            rows={data?.runs ?? []}
            loading={loading}
            getRowKey={(r) => r.id}
            emptyMessage="Nenhuma execução registrada."
          />
        </CardBody>
      </Card>

      {/* Restauração — a ação mais destrutiva do painel. */}
      <Modal
        open={!!toRestore}
        onClose={() => { setToRestore(null); setConfirmation('') }}
        title="Restaurar a base de dados"
        size="md"
        footer={
          <>
            <Button variant="ghost" onClick={() => { setToRestore(null); setConfirmation('') }} disabled={busy}>
              Cancelar
            </Button>
            <Button variant="danger" loading={busy}
              disabled={confirmation.trim() !== RESTORE_CONFIRMATION}
              onClick={() => void confirmRestore()}>
              Restaurar agora
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="flex items-start gap-3 rounded-md bg-rose-50 px-3 py-2.5 text-xs text-rose-900 ring-1 ring-rose-200">
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-rose-600" />
            <div>
              <p className="font-medium">Toda a base atual será substituída.</p>
              <p className="mt-0.5">
                Tudo que foi cadastrado depois deste arquivo se perde: faturas, assinantes,
                atendimentos. O sistema fica instável enquanto a restauração roda.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3 rounded-md bg-emerald-50 px-3 py-2.5 text-xs text-emerald-900 ring-1 ring-emerald-200">
            <ShieldCheck size={16} className="mt-0.5 shrink-0 text-emerald-600" />
            <p>
              Antes de substituir, o sistema gera um backup do estado atual e o guarda no bucket.
              Se algo der errado, é por ele que se volta atrás. Se esse backup falhar, a
              restauração não acontece.
            </p>
          </div>

          <dl className="rounded-md bg-gray-50 px-3 py-2.5 text-xs">
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Arquivo</dt>
              <dd className="font-mono text-gray-800">{toRestore?.name}</dd>
            </div>
            <div className="mt-1 flex justify-between gap-4">
              <dt className="text-gray-500">Gerado em</dt>
              <dd className="text-gray-800">{toRestore?.createdAt ? dateTimeBR(toRestore.createdAt) : '—'}</dd>
            </div>
            <div className="mt-1 flex justify-between gap-4">
              <dt className="text-gray-500">Tamanho</dt>
              <dd className="text-gray-800">{humanSize(toRestore?.size)}</dd>
            </div>
          </dl>

          <Input
            label={`Digite ${RESTORE_CONFIRMATION} para confirmar`}
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            placeholder={RESTORE_CONFIRMATION}
            autoComplete="off"
          />
        </div>
      </Modal>

      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={() => void confirmDelete()}
        loading={busy}
        variant="danger"
        confirmLabel="Excluir"
        title="Excluir backup"
        description={`O arquivo ${toDelete?.name ?? ''} sai do bucket e não tem como ser recuperado.`}
      />
    </>
  )
}
